import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import LinearProgress from '@mui/material/LinearProgress';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ConfirmDialog from '../../../../components/ConfirmDialog/ConfirmDialog';
import TrialOutreachPill from '../../trial-outreach/components/TrialOutreachPill';
import useProspectBulkSend from '../hooks/useProspectBulkSend';
import {
  BULK_DISTRIBUTIONS, BULK_INTERVAL_SECONDS, MESSAGE_VARIANTS, assignVariants, defaultMessage, labelOf,
} from '../prospectOptions';

const VARIANT_TONES = { conversa: 'blue', link: 'orange', retorno: 'green' };
const boxSx = { p: 1.5, borderRadius: '8px', bgcolor: '#F8F9FC', border: '1px solid #E8ECF0' };

/**
 * ProspectBulkMessageDialog - Envia uma mensagem a vários contatos pelo número da
 * Gabriela, um por vez e com intervalo: a primeira (distribuindo as versões do teste
 * A/B) ou o Retorno para quem não respondeu.
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Array<Object>} props.prospects selecionados (todos com WhatsApp)
 * @param {string} [props.initialDistribution] 'split' (padrão), 'conversa', 'link' ou 'retorno'
 * @param {Function} props.onSent recebe o contato atualizado a cada envio
 * @param {Function} props.onEditTemplates abre a edição dos textos
 * @param {Function} props.onClose recebe true quando o lote terminou
 */
function ProspectBulkMessageDialog({ isOpen, prospects, initialDistribution = 'split', onSent, onEditTemplates, onClose }) {
  const [distribution, setDistribution] = useState(initialDistribution);
  const [openPreview, setOpenPreview] = useState('');
  const { state, start, stop, reset } = useProspectBulkSend(onSent);
  const queue = useMemo(() => assignVariants(prospects, distribution), [prospects, distribution]);
  const usedVariants = MESSAGE_VARIANTS.filter((item) => queue.some((row) => row.variant === item.value));
  const [minWait, maxWait] = BULK_INTERVAL_SECONDS;
  const minutes = Math.ceil(((queue.length - 1) * (minWait + maxWait)) / 2 / 60);

  useEffect(() => {
    if (!isOpen) return;
    setDistribution(initialDistribution);
    setOpenPreview('');
    reset();
  }, [isOpen, initialDistribution, reset]);

  // Clique fora ou Esc não interrompe um lote em andamento: só o "Parar o envio"
  const handleClose = () => {
    if (state.running) return;
    onClose(state.finished);
  };

  const handleConfirm = () => {
    if (state.finished) {
      onClose(true);
      return;
    }
    start(queue);
  };

  const done = state.sent + state.failed.length;

  return (
    <ConfirmDialog
      isOpen={isOpen}
      title={`${distribution === 'retorno' ? 'Retorno' : 'Mensagem'} para ${prospects.length} contatos`}
      variant="info"
      confirmLabel={state.running ? 'Enviando...' : state.finished ? 'Fechar' : `Enviar ${prospects.length} pelo WhatsApp da Gabriela`}
      cancelLabel={state.finished ? 'Fechar' : 'Cancelar'}
      onConfirm={handleConfirm}
      onCancel={handleClose}
      confirmDisabled={state.running || prospects.length === 0}
      maxWidth={640}
    >
      {/* O ConfirmDialog não rola: o conteúdo rola por dentro para os botões ficarem à vista */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: '60vh', overflowY: 'auto', pr: 0.5 }}>
        {!state.running && !state.finished && (
          <Box>
            <ToggleButtonGroup value={distribution} exclusive size="small" onChange={(e, value) => value && setDistribution(value)}>
              {BULK_DISTRIBUTIONS.map((item) => (
                <ToggleButton key={item.value} value={item.value} sx={{ px: 2, textTransform: 'none' }}>
                  {item.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Sai um por vez, com {minWait} a {maxWait} segundos entre um e outro (cerca de {minutes} min no total).
              Deixe esta aba aberta até terminar.
            </Typography>
          </Box>
        )}

        {(state.running || state.finished) && (
          <Box>
            <LinearProgress variant="determinate" value={(done / queue.length) * 100} sx={{ borderRadius: 4, height: 6, mb: 1 }} />
            <Typography variant="body2">
              {state.finished
                ? `Pronto: ${state.sent} enviada(s)${state.failed.length ? `, ${state.failed.length} com falha` : ''}${done < queue.length ? `, ${queue.length - done} não enviada(s) porque o envio foi parado` : ''}.`
                : state.waitLeft > 0
                  ? `${done} de ${queue.length} enviadas. Próxima em ${state.waitLeft} s.`
                  : `Enviando para ${state.current || '...'} (${done + 1} de ${queue.length}).`}
            </Typography>
            {state.running && (
              <Button size="small" color="error" onClick={stop} sx={{ mt: 1, textTransform: 'none', fontWeight: 600 }}>
                Parar o envio
              </Button>
            )}
          </Box>
        )}

        {state.failed.length > 0 && (
          <Alert severity="warning">
            {state.failed.map((item) => <div key={item.name}>{item.name}: {item.error}</div>)}
          </Alert>
        )}

        <Box sx={{ ...boxSx, maxHeight: 200, overflowY: 'auto', bgcolor: '#fff' }}>
          {queue.map(({ prospect, variant }) => (
            <Box key={prospect.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
              <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>{prospect.name}</Typography>
              {prospect.messages_count > 0 && variant !== 'retorno' && <TrialOutreachPill tone="grey" label="Já recebeu" />}
              <TrialOutreachPill tone={VARIANT_TONES[variant]} label={labelOf(MESSAGE_VARIANTS, variant)} />
            </Box>
          ))}
        </Box>

        {!state.running && !state.finished && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Textos</Typography>
            <Button size="small" onClick={onEditTemplates} sx={{ textTransform: 'none', fontWeight: 600 }}>
              Editar os textos
            </Button>
          </Box>
        )}

        {!state.running && !state.finished && queue.length > 0 && usedVariants.map((item) => {
          const sample = queue.find((row) => row.variant === item.value).prospect;
          const open = openPreview === item.value;
          return (
            <Box key={item.value}>
              <Button
                size="small"
                onClick={() => setOpenPreview(open ? '' : item.value)}
                endIcon={<ExpandMoreIcon fontSize="small" sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />}
                sx={{ textTransform: 'none', fontWeight: 600, px: 0 }}
              >
                Ver o texto "{item.label}"
              </Button>
              <Collapse in={open} unmountOnExit>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Exemplo para {sample.name}
                </Typography>
                <Typography variant="body2" sx={{ ...boxSx, whiteSpace: 'pre-wrap' }}>{defaultMessage(sample, item.value)}</Typography>
              </Collapse>
            </Box>
          );
        })}
      </Box>
    </ConfirmDialog>
  );
}

export default ProspectBulkMessageDialog;
